import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight, Linkedin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeToggle } from "./ThemeToggle";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { SERVICES } from "../lib/data";

const LINKS = [
  { to: "/about", label: "About" },
  { to: "/founders-column", label: "Founder's Column" },
  { to: "/industries", label: "Sectors" },
  { to: "/case-studies", label: "Clients" },
  { to: "/insights", label: "Insights" },
];

export const Logo = ({ className = "", light = false }) => (
  <Link to="/" data-testid="nav-logo" className={`flex items-center gap-3 ${className}`}>
    <img src="/brand/mark.png" alt="Zenspark Global Corporation" className="h-9 w-9 rounded-md object-contain" />
    <span className={`hidden font-display text-[15px] font-bold leading-tight tracking-tight sm:block ${light ? "text-white" : "text-primary"}`}>
      Zenspark Global<br /><span className="font-semibold text-accent">Corporation</span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass border-b border-border" : "border-b border-transparent"
      }`}
    >
      <nav className="container-x flex h-[72px] items-center justify-between">
        <Logo />

        <div className="hidden items-center gap-1 lg:flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-services-trigger"
                className="flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-accent"
              >
                Services <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-2">
              {SERVICES.map((s) => (
                <DropdownMenuItem key={s.slug} asChild>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`nav-service-${s.slug}`}
                    className="flex cursor-pointer flex-col items-start gap-0.5 rounded-lg px-3 py-2.5"
                  >
                    <span className="font-medium">{s.title}</span>
                    <span className="text-xs text-muted-foreground">{s.tag}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors hover:text-accent ${
                  isActive ? "text-accent" : "text-foreground/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <a
            href="https://www.linkedin.com/company/zenspark-global-corporation-pvt-ltd"
            target="_blank" rel="noreferrer"
            data-testid="nav-linkedin"
            aria-label="Follow Zenspark on LinkedIn"
            className="hidden h-10 w-10 place-items-center rounded-full border border-border text-foreground transition-colors hover:border-accent hover:text-accent sm:grid"
          >
            <Linkedin className="h-4 w-4" />
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="group hidden items-center gap-1.5 rounded-full brand-gradient animate-gradient px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.04] sm:flex"
          >
            Schedule Consultation
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="grid h-10 w-10 place-items-center rounded-full border border-border lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden glass border-b border-border lg:hidden"
          >
            <div className="container-x flex flex-col gap-1 py-4">
              <Link to="/services" className="rounded-lg px-3 py-3 text-sm font-medium">Services</Link>
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="rounded-lg px-3 py-3 text-sm font-medium">
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="mt-2 rounded-full brand-gradient px-5 py-3 text-center text-sm font-semibold text-white"
              >
                Schedule Consultation
              </Link>
              <a
                href="https://www.linkedin.com/company/zenspark-global-corporation-pvt-ltd"
                target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 rounded-full border border-border px-5 py-3 text-center text-sm font-semibold"
              >
                <Linkedin className="h-4 w-4" /> Follow on LinkedIn
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
